import type { RoiId } from '~/domain/changes/ids';
import { domainEventBus } from '~/domain/events/DomainEventBus';
import {
    ContourRedoRequestedEvent,
    ContourUndoRequestedEvent,
    RoiContoursModifiedEvent,
} from '~/domain/events/RoiDomainEvents';
import type { DomainEventSubscription } from '~/domain/events/DomainEventBus';
import type { Patient } from '~/domain/patient/patient';
import type { ContourService } from '~/domain/services/structureSet/ContourService';
import type { ContourStepMove, ContourUndoService } from '~/domain/undo';

/**
 * Routes engine-side contour geometry changes into the domain change
 * tracker, so a mask edit is dirty state exactly like a rename.
 *
 * Every mask mutation funnels through `TextureWrite.finish()` /
 * `RegionOfInterest.replaceTexture()` (brush, line, boolean, margin, ring,
 * smoothing, transforms, auto-suggestion) and publishes
 * {@link RoiContoursModifiedEvent}; undo/redo restores publish
 * {@link ContourUndoRequestedEvent} / {@link ContourRedoRequestedEvent}
 * instead. Subscribing to all three here is the single funnel — no
 * per-tool wiring, so a new drawing tool cannot silently bypass dirty
 * tracking.
 *
 * Each event is resolved in the structure set its `structureSetUid` names,
 * never in whichever set is selected. A set or ROI that does not resolve
 * is reported via console.error (telemetry), never swallowed.
 *
 * Each change names its place in the ROI's contour history: an edit, the
 * step it left, which the history has not recorded the edit over yet when
 * the event arrives; a restore, the steps its event carries.
 *
 * Created and disposed with the {@link PatientSession}.
 */
export class ContourEditBridge {
    private readonly subscriptions: DomainEventSubscription[];

    constructor(
        private readonly patient: Pick<
            Patient,
            'getStructureSetWithSopInstanceUid'
        >,
        private readonly contourService: ContourService,
        private readonly contourHistory: Pick<ContourUndoService, 'currentStep'>
    ) {
        this.subscriptions = [
            domainEventBus.subscribe<RoiContoursModifiedEvent>(
                RoiContoursModifiedEvent.EVENT_TYPE,
                (event) => this.commit(event, { kind: 'edit' })
            ),
            domainEventBus.subscribe<ContourUndoRequestedEvent>(
                ContourUndoRequestedEvent.EVENT_TYPE,
                (event) =>
                    this.commit(event, { kind: 'revert', steps: event.steps })
            ),
            domainEventBus.subscribe<ContourRedoRequestedEvent>(
                ContourRedoRequestedEvent.EVENT_TYPE,
                (event) =>
                    this.commit(event, { kind: 'revert', steps: event.steps })
            ),
        ];
    }

    dispose(): void {
        for (const subscription of this.subscriptions) {
            subscription.unsubscribe();
        }
    }

    private commit(
        event: { readonly structureSetUid: string; readonly roiId: RoiId },
        change:
            | { kind: 'edit' }
            | { kind: 'revert'; steps: ContourStepMove | undefined }
    ): void {
        const structureSet = this.patient.getStructureSetWithSopInstanceUid(
            event.structureSetUid
        );
        const roi = structureSet?.getRoiById(event.roiId);
        if (!structureSet || !roi) {
            // Fail loud: an untracked contour edit means the unsaved-changes
            // indication could show "nothing to save" for changed geometry.
            console.error(
                'ContourEditBridge: contour event for an ROI not present ' +
                    'in the structure set it names; the edit is NOT dirty-tracked'
            );
            return;
        }
        if (change.kind === 'edit') {
            this.contourService.commitContourEdit(
                { id: roi.id },
                [],
                this.contourHistory.currentStep(roi.id)
            );
        } else {
            this.contourService.commitContourRevert(
                { id: roi.id },
                change.steps ?? null
            );
        }
    }
}
